import {
  Box,
  Button,
  Checkbox,
  Divider,
  Grid,
  Link as MuiLink,
  Pagination,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { AlertContext } from "contexts/Alert";
import { BentoCategoriesContext } from "contexts/BentoCategories";
import {
  CAMPAIGNS_PER_PAGE,
  CampaignsContext,
  DeleteCampaignsResult,
} from "contexts/Campaigns";
import { DiscoverTabView, DiscoverViewContext } from "contexts/DiscoverView";
import { NewCampaignProvider } from "contexts/NewCampaign";
import { OnboardingDialogsContext } from "contexts/OnboardingDialogs";
import { OrganizationUserContext } from "contexts/Organization";
import { SubscriptionContext } from "contexts/Subscription";
import { useContext, useEffect, useState } from "react";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import {
  Campaign,
  CampaignStats,
  CampaignStatus,
  UpgradeDialogSource,
} from "schemas/dashboard";
import { routes } from "schemas/routes";

import ConfirmDialog from "components/Dialogs/ConfirmDialog";
import { PAGE_VISITED } from "constants/trackingProps";
import { displayLockedCampaignWall, formatDate } from "utils/time";
import { trackEvent } from "utils/tracking";

import ArchiveCampaignDialog from "./ArchiveCampaignDialog";
import CampaignActionsMenu from "./CampaignActionsMenu";
import CampaignCard from "./CampaignCard";
import CampaignDeleteButton from "./CampaignDeleteButton";
import CampaignDetail from "./CampaignDetail";
import CampaignFilterChips from "./CampaignFilterChips";
import CampaignRestoreButton from "./CampaignRestoreButton";
import { CampaignStatusChip } from "./CampaignStatusChip";
import NewCampaign from "./NewCampaign";
import NoCampaignsYet from "./NoCampaignYet";
import CampaignPreviewPage from "./PreviewPage";
import RenameCampaignDialog from "./RenameCampaignDialog";
import ResumeCampaignDialog from "./ResumeCampaignDialog";

const PAUSE_DIALOG_BODY =
  "Pausing cancels all unsent outreach emails in this campaign. " +
  "Follow-ups for outreach that's already been sent will still go out. " +
  "When you resume, you'll pick a new start date and we'll reschedule the remaining brands.";

const headerCellSx = {
  backgroundColor: "#f1f0ec",
  fontWeight: 600,
  fontSize: 11,
  color: "#4A5565",
  textTransform: "uppercase" as const,
  letterSpacing: 0.5,
};

type CampaignTab = "active" | "archived";

const TABS: { value: CampaignTab; label: string; icon: string }[] = [
  { value: "active", label: "Active", icon: "fa-regular fa-bolt" },
  { value: "archived", label: "Archived", icon: "fa-regular fa-box-archive" },
];

interface CampaignRowProps {
  campaign: Campaign;
  stats?: CampaignStats;
  statsLoading: boolean;
  isArchived: boolean;
  selected: boolean;
  onToggle: (id: number) => void;
  onDelete: (campaign: Campaign) => void;
  onPause: (campaign: Campaign) => void;
  onResume: (campaign: Campaign) => void;
  onArchive: (campaign: Campaign) => void;
  onDuplicate: (campaign: Campaign) => void;
  onRestore: (campaign: Campaign) => void;
  onRename: (campaign: Campaign) => void;
  onEditSettings: (campaign: Campaign) => void;
  onRowClick: (campaign: Campaign) => void;
  onOpenAssistant: (campaign: Campaign) => void;
}

function CampaignRow({
  campaign,
  stats,
  statsLoading,
  isArchived,
  selected,
  onToggle,
  onDelete,
  onPause,
  onResume,
  onArchive,
  onDuplicate,
  onRestore,
  onRename,
  onEditSettings,
  onRowClick,
  onOpenAssistant,
}: CampaignRowProps) {
  const { subscription, setUpgradeDialogSource } =
    useContext(SubscriptionContext);

  const dash = "—";
  const opensPct = ((stats?.openedPercentage || 0) * 100).toFixed(1);
  const respondedPct = ((stats?.respondedPercentage || 0) * 100).toFixed(1);

  return (
    <TableRow
      hover
      selected={selected}
      onClick={() => onRowClick(campaign)}
      sx={{
        cursor: "pointer",
        "&.Mui-selected": { backgroundColor: "rgba(99,102,241,0.04)" },
        "&.Mui-selected:hover": { backgroundColor: "rgba(99,102,241,0.07)" },
      }}
    >
      {/* Checkbox */}
      {!isArchived && (
        <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
          <Checkbox
            size="small"
            checked={selected}
            onChange={() => onToggle(campaign.id!)}
          />
        </TableCell>
      )}

      {/* Campaign Name */}
      <TableCell
        sx={{ textDecoration: "none", color: "inherit", display: "table-cell" }}
      >
        <Typography noWrap sx={{ fontSize: 14, minWidth: 0 }}>
          {campaign.name}
        </Typography>
        <CampaignFilterChips searchParameters={campaign.searchParameters} />
      </TableCell>

      {/* Status */}
      <TableCell sx={{ display: "table-cell" }}>
        {campaign?.status && campaign?.startDate ? (
          <CampaignStatusChip
            status={campaign.status}
            startDate={campaign.startDate}
          />
        ) : (
          <Typography sx={{ fontSize: 13, color: grey[400] }}>—</Typography>
        )}
      </TableCell>

      {/* Date Range */}
      <TableCell
        sx={{ textDecoration: "none", color: "inherit", display: "table-cell" }}
      >
        {campaign?.startDate || campaign?.endDate ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <Box
                component="i"
                className="fa-regular fa-calendar"
                sx={{ fontSize: 11, color: grey[400] }}
              />
              <Typography sx={{ fontSize: 13, whiteSpace: "nowrap" }}>
                {formatDate(campaign?.startDate)}
                {campaign?.endDate && (
                  <> &ndash; {formatDate(campaign.endDate)}</>
                )}
              </Typography>
            </Box>
            {(campaign?.timezone || campaign?.emailsPerDay) && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  flexWrap: "wrap",
                }}
              >
                {campaign?.timezone && (
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.5,
                      bgcolor: grey[100],
                      border: "1px solid",
                      borderColor: grey[300],
                      borderRadius: 1.5,
                      px: 1,
                      py: 0.25,
                      fontSize: 11,
                      color: grey[700],
                      fontWeight: 500,
                    }}
                  >
                    <Box
                      component="i"
                      className="fa-regular fa-clock"
                      sx={{ fontSize: 10 }}
                    />
                    {campaign.timezone}
                  </Box>
                )}
                {campaign?.emailsPerDay && (
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.5,
                      bgcolor: "purple.light",
                      border: "1px solid",
                      borderColor: "purple.main",
                      borderRadius: 1.5,
                      px: 1,
                      py: 0.25,
                      fontSize: 11,
                      color: "purple.dark",
                      fontWeight: 600,
                    }}
                  >
                    <Box
                      component="i"
                      className="fa-regular fa-envelope"
                      sx={{ fontSize: 10 }}
                    />
                    {campaign.emailsPerDay} emails/day
                  </Box>
                )}
              </Box>
            )}
          </Box>
        ) : (
          <Typography sx={{ fontSize: 13, color: grey[400] }}>
            {dash}
          </Typography>
        )}
      </TableCell>

      {/* Brands */}
      <TableCell
        align="center"
        sx={{ textDecoration: "none", color: "inherit", display: "table-cell" }}
      >
        <Typography sx={{ fontSize: 14 }}>
          {campaign.campaignBrandCount ?? dash}
        </Typography>
      </TableCell>

      {/* Sent */}
      <TableCell
        align="center"
        sx={{ textDecoration: "none", color: "inherit", display: "table-cell" }}
      >
        <Typography sx={{ fontSize: 14 }}>
          {statsLoading ? dash : stats?.outreachCount ?? dash}
        </Typography>
      </TableCell>

      {/* Opens */}
      <TableCell
        align="center"
        sx={{ textDecoration: "none", color: "inherit", display: "table-cell" }}
      >
        {!subscription?.isSubscribed ? (
          <MuiLink
            onClick={(e) => {
              e.preventDefault();
              setUpgradeDialogSource(UpgradeDialogSource.email_tracking);
            }}
            sx={{ fontSize: 12 }}
          >
            Upgrade
          </MuiLink>
        ) : (
          <Typography sx={{ fontSize: 14 }}>
            {statsLoading ? dash : `${stats?.openedCount || 0} (${opensPct}%)`}
          </Typography>
        )}
      </TableCell>

      {/* Responses */}
      <TableCell
        align="center"
        sx={{ textDecoration: "none", color: "inherit", display: "table-cell" }}
      >
        <Typography sx={{ fontSize: 14 }}>
          {statsLoading
            ? dash
            : `${stats?.respondedCount || 0} (${respondedPct}%)`}
        </Typography>
      </TableCell>

      {/* Action */}
      <TableCell align="center" onClick={(e) => e.stopPropagation()}>
        {isArchived ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.5,
            }}
          >
            <CampaignRestoreButton onRestore={() => onRestore(campaign)} />
            <CampaignDeleteButton
              hasSentEmails={campaign.hasSentEmails}
              onDelete={() => onDelete(campaign)}
            />
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.5,
            }}
          >
            <Tooltip title="Start AI chat about this campaign" placement="top">
              <Button
                size="small"
                variant="outlined"
                disableElevation
                onClick={() => onOpenAssistant(campaign)}
                startIcon={
                  <Box
                    component="i"
                    className="fa-solid fa-sparkles"
                    sx={{ fontSize: 12, color: "#00875a" }}
                  />
                }
                sx={{
                  textTransform: "none",
                  minWidth: 0,
                  px: 1.25,
                  py: 0.25,
                  fontSize: 12,
                  fontWeight: 700,
                  borderRadius: 999,
                  color: "#00875a",
                  backgroundColor: "#dcfce7",
                  borderColor: "#a7f3d0",
                  "&:hover": {
                    backgroundColor: "#bbf7d0",
                    borderColor: "#86efac",
                  },
                }}
              >
                AI
              </Button>
            </Tooltip>
            <CampaignActionsMenu
              campaign={campaign}
              onRename={() => onRename(campaign)}
              onEditSettings={() => onEditSettings(campaign)}
              onPause={() => onPause(campaign)}
              onResume={() => onResume(campaign)}
              onDuplicate={() => onDuplicate(campaign)}
              onArchive={() => onArchive(campaign)}
              onDelete={() => onDelete(campaign)}
            />
          </Box>
        )}
      </TableCell>
    </TableRow>
  );
}

export default function AllCampaigns() {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { currentOrg } = useContext(OrganizationUserContext);
  const { setCompleteOnboardingDialogOpen, incompleteProfile, noTemplates } =
    useContext(OnboardingDialogsContext);
  const { discoverTab, setDiscoverTab, openAssistant } =
    useContext(DiscoverViewContext);
  const { topLevelCategories, fetchBentoCategories } = useContext(
    BentoCategoriesContext,
  );
  const _isMobileScreen = useMediaQuery(theme.breakpoints.down("md"));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down("xl"));
  const isMobileScreen =
    _isMobileScreen || (isMediumScreen && discoverTab !== null);

  const incompleteOnboarding = incompleteProfile || noTemplates;

  const {
    campaigns,
    getCampaigns,
    campaignsLoading,
    campaignsCount,
    campaignPage,
    campaignStatsLoading,
    campaignStats,
    deleteCampaign,
    pauseCampaign,
    resumeCampaign,
    archiveCampaign,
    restoreCampaign,
    duplicateCampaign,
  } = useContext(CampaignsContext);
  const { setAlert } = useContext(AlertContext);

  const [tab, setTab] = useState<CampaignTab>("active");
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [singleDeleteTarget, setSingleDeleteTarget] = useState<Campaign | null>(
    null,
  );
  const [pauseTarget, setPauseTarget] = useState<Campaign | null>(null);
  const [resumeTarget, setResumeTarget] = useState<Campaign | null>(null);
  const [archiveTarget, setArchiveTarget] = useState<Campaign | null>(null);
  const [renameTarget, setRenameTarget] = useState<Campaign | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  const isArchivedTab = tab === "archived";

  useEffect(() => {
    if (campaignsCount === null && !campaignsLoading) getCampaigns(1, false);
    if (currentOrg?.id && topLevelCategories.length === 0) {
      fetchBentoCategories();
    }
    trackEvent(PAGE_VISITED);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id]);

  const handleTabChange = (next: CampaignTab) => {
    if (next === tab) return;
    setTab(next);
    setSelected(new Set());
    getCampaigns(1, next === "archived");
  };

  const openUploadBrands = () => {
    trackEvent("Upload Brands Button Clicked");
    setDiscoverTab(DiscoverTabView.UPLOAD_BRANDS);
  };

  const createCampaignClicked = () => {
    if (incompleteOnboarding && !currentOrg?.isBrand) {
      trackEvent("New Campaign Button Clicked - Complete Onboarding Modal");
      setCompleteOnboardingDialogOpen(true);
      return;
    }
    navigate(`/${routes.campaignView}/new`);
    trackEvent("New Campaign Button Clicked");
  };

  const toggleRow = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const allSelected =
    campaigns.length > 0 && campaigns.every((c) => selected.has(c.id!));
  const someSelected = campaigns.some((c) => selected.has(c.id!));

  const toggleAll = () => {
    if (allSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(campaigns.map((c) => c.id!)));
    }
  };

  const announceDeleteResult = (result: DeleteCampaignsResult | null) => {
    if (!result) return;
    const parts: string[] = [];
    if (result.deleted.length) parts.push(`${result.deleted.length} deleted`);
    if (result.archived.length) {
      parts.push(`${result.archived.length} archived (already had sends)`);
    }
    if (parts.length) setAlert(parts.join(" · "), "success");
  };

  const handleBulkDelete = async () => {
    setDeleteLoading(true);
    const result = await deleteCampaign(Array.from(selected));
    setSelected(new Set());
    setBulkDeleteOpen(false);
    setDeleteLoading(false);
    announceDeleteResult(result);
  };

  const handleSingleDelete = async () => {
    if (!singleDeleteTarget?.id) return;
    setDeleteLoading(true);
    const result = await deleteCampaign([singleDeleteTarget.id]);
    setSingleDeleteTarget(null);
    setDeleteLoading(false);
    announceDeleteResult(result);
  };

  const handlePauseConfirm = async () => {
    if (!pauseTarget?.id) return;
    setActionLoading(true);
    const ok = await pauseCampaign(pauseTarget.id);
    if (ok) setAlert("Campaign paused", "success");
    setPauseTarget(null);
    setActionLoading(false);
  };

  const handleResumeConfirm = async (startDate: string) => {
    if (!resumeTarget?.id) return;
    setActionLoading(true);
    const ok = await resumeCampaign(resumeTarget.id, startDate);
    if (ok) setAlert("Campaign resumed", "success");
    setResumeTarget(null);
    setActionLoading(false);
  };

  const archiveAndRefresh = async (campaign: Campaign, pauseFirst: boolean) => {
    if (!campaign.id) return;
    setActionLoading(true);
    const ok = await archiveCampaign(campaign.id, pauseFirst);
    if (ok) {
      setAlert("Campaign archived", "success");
      getCampaigns(campaignPage, false);
    }
    setActionLoading(false);
  };

  // Only in-progress campaigns have scheduled pitches to stop, so they get the
  // pause-or-archive dialog; everything else archives directly.
  const handleArchive = (campaign: Campaign) => {
    if (campaign.status === CampaignStatus.in_progress) {
      setArchiveTarget(campaign);
    } else {
      archiveAndRefresh(campaign, false);
    }
  };

  const handleArchiveConfirm = async (pauseFirst: boolean) => {
    if (!archiveTarget) return;
    await archiveAndRefresh(archiveTarget, pauseFirst);
    setArchiveTarget(null);
  };

  const handleRestore = async (campaign: Campaign) => {
    if (!campaign.id) return;
    const ok = await restoreCampaign(campaign.id);
    if (ok) {
      setAlert("Campaign restored", "success");
      getCampaigns(campaignPage, true);
    }
  };

  const handleDuplicate = async (campaign: Campaign) => {
    if (!campaign.id) return;
    const created = await duplicateCampaign(campaign.id);
    if (created?.id) {
      setAlert("Campaign duplicated — finish setting it up", "success");
      navigate(`campaign/${created.id}`);
    }
  };

  const handleOpenAssistantForCampaign = (campaign: Campaign) => {
    if (!campaign.id) return;
    openAssistant({
      promptPage: "campaigns",
      entity: { entityType: "campaign", entityId: campaign.id },
      source: "Campaign Card CTA",
    });
  };

  if (!currentOrg?.isPaidUser && displayLockedCampaignWall()) {
    return <CampaignPreviewPage />;
  }

  const isCampaignDetailRoute = new RegExp(`/${routes.campaignView}/\\d+`).test(
    location.pathname,
  );
  if (isCampaignDetailRoute) {
    return (
      <Routes>
        <Route path=":campaignId/*" element={<CampaignDetail />} />
      </Routes>
    );
  }

  return (
    <Box
      sx={{
        pl: { xs: 0.5, md: 1 },
        pr: { xs: 0.5, md: 0.5 },
        pt: 1,
        pb: 4,
        height: "100%",
        overflowY: "auto",
      }}
    >
      <Box sx={{ bgcolor: "#fff", borderRadius: 3, p: { xs: 2, md: 3 } }}>
        <Grid
          container
          alignItems="center"
          justifyContent="space-between"
          gap={2}
        >
          <Grid item xs="auto">
            <Typography variant="h3" sx={{ fontWeight: 700 }}>
              My Campaigns
            </Typography>
            <Typography sx={{ fontSize: 14, color: "#4A5565", mt: 0.5 }}>
              Organize your brand outreach into targeted campaigns
            </Typography>
          </Grid>
          <Grid
            item
            xs="auto"
            sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
          >
            {someSelected && (
              <Button
                size="small"
                variant="outlined"
                disableElevation
                onClick={() => setBulkDeleteOpen(true)}
                startIcon={
                  <Box
                    component="i"
                    className="fa-regular fa-trash"
                    sx={{ fontSize: 12 }}
                  />
                }
                sx={{
                  borderRadius: 9999,
                  textTransform: "none",
                  color: "#c0392b",
                  borderColor: "#f1c4bf",
                  fontSize: 12,
                  fontWeight: 500,
                  height: 30,
                  px: 1.5,
                  "&:hover": {
                    borderColor: "#e3a59e",
                    backgroundColor: "#fdf3f2",
                  },
                }}
              >
                Delete {selected.size} campaign{selected.size > 1 ? "s" : ""}
              </Button>
            )}
            {!isMobileScreen && (
              <Button
                size="small"
                variant="outlined"
                disableElevation
                sx={{
                  borderRadius: 9999,
                  textTransform: "none",
                  color: "#364153",
                  borderColor: "#e5e7eb",
                  fontSize: 12,
                  fontWeight: 500,
                  height: 30,
                  px: 1.5,
                  "&:hover": {
                    borderColor: "#d1d5db",
                    backgroundColor: "#f9fafb",
                  },
                }}
                onClick={openUploadBrands}
              >
                Upload Brands{" "}
                <Box
                  component="i"
                  className="fa-regular fa-upload"
                  sx={{ ml: 0.5 }}
                />
              </Button>
            )}
            <Button
              size="small"
              variant="contained"
              disableElevation
              onClick={createCampaignClicked}
              sx={{
                borderRadius: 9999,
                textTransform: "none",
                backgroundColor: "#009966",
                color: "#fff",
                fontSize: 12,
                fontWeight: 500,
                height: 30,
                px: 1.5,
                "&:hover": {
                  backgroundColor: "#00875a",
                },
              }}
            >
              <Box
                component="i"
                className="fa-solid fa-plus"
                sx={{ mr: 0.5, fontSize: 11 }}
              />
              Create Campaign
            </Button>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        <Box
          sx={{
            display: "inline-flex",
            gap: 0.5,
            p: 0.5,
            borderRadius: 2,
            backgroundColor: grey[200],
            mb: 2,
          }}
        >
          {TABS.map((t) => {
            const isSelected = tab === t.value;
            return (
              <Box
                key={t.value}
                onClick={() => handleTabChange(t.value)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  cursor: "pointer",
                  px: 1.75,
                  py: 0.625,
                  borderRadius: 1.5,
                  fontSize: 12,
                  fontWeight: 500,
                  color: isSelected ? "text.primary" : grey[700],
                  backgroundColor: isSelected ? "#fff" : "transparent",
                  boxShadow: isSelected ? "0 1px 2px rgba(0,0,0,0.12)" : "none",
                }}
              >
                <Box component="i" className={t.icon} sx={{ fontSize: 11 }} />
                {t.label}
              </Box>
            );
          })}
        </Box>

        {campaignsLoading || campaigns.length > 0 ? (
          <>
            {isMobileScreen ? (
              <Box
                sx={{
                  mt: 2,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                  pb: 10,
                }}
              >
                {campaignsLoading ? (
                  <>
                    <Skeleton variant="rounded" width="100%" height={120} />
                    <Skeleton variant="rounded" width="100%" height={120} />
                    <Skeleton variant="rounded" width="100%" height={120} />
                  </>
                ) : (
                  campaigns.map((campaign) => (
                    <Box key={campaign.id}>
                      <CampaignCard
                        campaign={campaign}
                        stats={campaignStats[campaign.id!]}
                        statsLoading={campaignStatsLoading[campaign.id!]}
                        isArchived={isArchivedTab}
                        onDelete={setSingleDeleteTarget}
                        onPause={setPauseTarget}
                        onResume={setResumeTarget}
                        onRename={setRenameTarget}
                        onEditSettings={(c) => navigate(`campaign/${c.id}`)}
                        onDuplicate={handleDuplicate}
                        onArchive={handleArchive}
                        onRestore={handleRestore}
                        onOpenAssistant={handleOpenAssistantForCampaign}
                      />
                    </Box>
                  ))
                )}
              </Box>
            ) : campaignsLoading ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Skeleton variant="rounded" width="100%" height={44} />
                <Skeleton variant="rounded" width="100%" height={56} />
                <Skeleton variant="rounded" width="100%" height={56} />
                <Skeleton variant="rounded" width="100%" height={56} />
              </Box>
            ) : (
              <TableContainer
                sx={{ border: `1px solid ${grey[200]}`, borderRadius: 2 }}
              >
                <Table
                  size="small"
                  sx={{
                    "& .MuiTableCell-root": {
                      px: 1.5,
                      py: 1.25,
                      borderBottom: `1px solid ${grey[100]}`,
                    },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      {!isArchivedTab && (
                        <TableCell padding="checkbox" sx={headerCellSx}>
                          <Checkbox
                            size="small"
                            checked={allSelected}
                            indeterminate={someSelected && !allSelected}
                            onChange={toggleAll}
                          />
                        </TableCell>
                      )}
                      <TableCell sx={headerCellSx}>Campaign Name</TableCell>
                      <TableCell sx={headerCellSx}>Status</TableCell>
                      <TableCell sx={headerCellSx}>Date Range</TableCell>
                      <TableCell align="center" sx={headerCellSx}>
                        Brands
                      </TableCell>
                      <TableCell align="center" sx={headerCellSx}>
                        Sent
                      </TableCell>
                      <TableCell align="center" sx={headerCellSx}>
                        Opens
                      </TableCell>
                      <TableCell align="center" sx={headerCellSx}>
                        Responses
                      </TableCell>
                      <TableCell align="center" sx={headerCellSx}>
                        Action
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {campaigns.map((campaign) => (
                      <CampaignRow
                        key={campaign.id}
                        campaign={campaign}
                        stats={campaignStats[campaign.id!]}
                        statsLoading={campaignStatsLoading[campaign.id!]}
                        isArchived={isArchivedTab}
                        selected={selected.has(campaign.id!)}
                        onToggle={toggleRow}
                        onDelete={setSingleDeleteTarget}
                        onPause={setPauseTarget}
                        onResume={setResumeTarget}
                        onArchive={handleArchive}
                        onDuplicate={handleDuplicate}
                        onRestore={handleRestore}
                        onRename={setRenameTarget}
                        onEditSettings={(c) => navigate(`campaign/${c.id}`)}
                        onOpenAssistant={handleOpenAssistantForCampaign}
                        onRowClick={(c) => {
                          trackEvent("Campaign Row Clicked");
                          const hasSends =
                            c.status === CampaignStatus.in_progress ||
                            c.status === CampaignStatus.paused ||
                            c.status === CampaignStatus.complete;
                          navigate(hasSends ? `${c.id}` : `campaign/${c.id}`);
                        }}
                      />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            {campaignsCount !== null &&
              campaignsCount > CAMPAIGNS_PER_PAGE &&
              !campaignsLoading && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-start",
                    mt: 2,
                  }}
                >
                  <Pagination
                    count={Math.ceil(campaignsCount! / CAMPAIGNS_PER_PAGE)}
                    page={campaignPage}
                    onChange={(_e, page) => getCampaigns(page, isArchivedTab)}
                  />
                </Box>
              )}
          </>
        ) : isArchivedTab ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1,
              py: 8,
              color: grey[500],
            }}
          >
            <Box
              component="i"
              className="fa-regular fa-box-archive"
              sx={{ fontSize: 28 }}
            />
            <Typography sx={{ fontSize: 14 }}>No archived campaigns</Typography>
          </Box>
        ) : (
          <NoCampaignsYet onCreateCampaign={createCampaignClicked} />
        )}
      </Box>

      <ConfirmDialog
        header="Delete Campaigns"
        subheader={`Delete ${selected.size} campaign${
          selected.size > 1 ? "s" : ""
        }? Any that have already sent emails will be moved to Archived instead — the rest are permanently deleted.`}
        open={bulkDeleteOpen}
        handleClose={() => setBulkDeleteOpen(false)}
        handleConfirm={handleBulkDelete}
        loading={deleteLoading}
      />

      <ConfirmDialog
        header="Delete Campaign"
        subheader={`Delete "${singleDeleteTarget?.name}"? If it has already sent emails it'll be moved to Archived instead; otherwise it's permanently deleted.`}
        open={Boolean(singleDeleteTarget)}
        handleClose={() => setSingleDeleteTarget(null)}
        handleConfirm={handleSingleDelete}
        loading={deleteLoading}
      />

      <ConfirmDialog
        header="Pause campaign"
        subheader={PAUSE_DIALOG_BODY}
        confirmText="Pause"
        open={Boolean(pauseTarget)}
        handleClose={() => setPauseTarget(null)}
        handleConfirm={handlePauseConfirm}
        loading={actionLoading}
      />

      <ResumeCampaignDialog
        campaign={resumeTarget}
        open={Boolean(resumeTarget)}
        loading={actionLoading}
        handleClose={() => setResumeTarget(null)}
        handleConfirm={handleResumeConfirm}
      />

      <ArchiveCampaignDialog
        campaign={archiveTarget}
        open={Boolean(archiveTarget)}
        loading={actionLoading}
        handleClose={() => setArchiveTarget(null)}
        handleConfirm={handleArchiveConfirm}
      />

      <RenameCampaignDialog
        campaign={renameTarget}
        open={Boolean(renameTarget)}
        handleClose={() => setRenameTarget(null)}
      />

      <Routes>
        <Route
          path="new/*"
          element={
            <NewCampaignProvider>
              <NewCampaign />
            </NewCampaignProvider>
          }
        />
        <Route
          path="campaign/:campaignId/*"
          element={
            <NewCampaignProvider>
              <NewCampaign />
            </NewCampaignProvider>
          }
        />
      </Routes>
    </Box>
  );
}
